/**
 * Centralized CLI install info registry.
 *
 * Single source of truth for CLI names, install commands per OS,
 * prerequisites, and auth requirements. Used by onboarding and settings.
 */

import type { DetectedOS } from "./detect-os";
import type { CliChoice } from "@/components/onboarding/cli-chooser-phases";
import { translate } from "@/i18n";
import { libLabelsMessages } from "@/i18n/messages/lib-labels";

/** `description` and `auth` read in the app language when they are read. */
export interface CliInstallInfo {
  id: CliChoice;
  name: string;
  description: string;
  prereqs: string[];
  auth: string;
  commands: Record<DetectedOS, string>;
  /** Canonical command to authenticate the CLI after install. */
  loginCommand: string;
  docsUrl: string;
}

/** Install the official Windows release without requiring a package manager. */
export const OPENCODE_WINDOWS_INSTALL_COMMAND = [
  "$ErrorActionPreference = 'Stop'",
  "$ocArch = if ($env:PROCESSOR_ARCHITECTURE -eq 'ARM64' -or $env:PROCESSOR_ARCHITEW6432 -eq 'ARM64') { 'arm64' } elseif ($env:PROCESSOR_ARCHITECTURE -eq 'AMD64' -or $env:PROCESSOR_ARCHITEW6432 -eq 'AMD64') { 'x64-baseline' } else { throw 'OpenCode requires 64-bit Windows.' }",
  "$ocDir = Join-Path $env:USERPROFILE '.opencode\\bin'",
  "$ocTmp = Join-Path ([IO.Path]::GetTempPath()) ([Guid]::NewGuid().ToString())",
  "New-Item -ItemType Directory -Path $ocTmp -Force | Out-Null",
  "try { Invoke-WebRequest -UseBasicParsing -Uri ('https://github.com/anomalyco/opencode/releases/latest/download/opencode-windows-' + $ocArch + '.zip') -OutFile (Join-Path $ocTmp 'opencode.zip'); Expand-Archive -LiteralPath (Join-Path $ocTmp 'opencode.zip') -DestinationPath (Join-Path $ocTmp 'release'); $ocExe = @(Get-ChildItem -LiteralPath (Join-Path $ocTmp 'release') -Filter 'opencode.exe' -File -Recurse); if ($ocExe.Count -ne 1) { throw 'The OpenCode release did not contain one executable.' }; New-Item -ItemType Directory -Path $ocDir -Force | Out-Null; Copy-Item -LiteralPath $ocExe[0].FullName -Destination (Join-Path $ocDir 'opencode.exe') -Force; $ocPath = [Environment]::GetEnvironmentVariable('Path', 'User'); if (($ocPath -split ';') -notcontains $ocDir) { [Environment]::SetEnvironmentVariable('Path', ($ocDir + ';' + $ocPath), 'User') }; $env:Path = $ocDir + ';' + $env:Path } finally { Remove-Item -LiteralPath $ocTmp -Recurse -Force }",
].join("; ");

export const CLI_REGISTRY: Record<CliChoice, CliInstallInfo> = {
  claude: {
    id: "claude",
    name: "Claude Code",
    get description() {
      return translate(libLabelsMessages, "claudeDescription");
    },
    prereqs: [],
    get auth() {
      return translate(libLabelsMessages, "claudeAuth");
    },
    commands: {
      macos: "curl -fsSL https://claude.ai/install.sh | bash",
      linux: "curl -fsSL https://claude.ai/install.sh | bash",
      windows: "irm https://claude.ai/install.ps1 | iex",
    },
    loginCommand: "claude auth login",
    docsUrl: "https://docs.anthropic.com/en/docs/claude-code",
  },
  codex: {
    id: "codex",
    name: "Codex",
    get description() {
      return translate(libLabelsMessages, "codexDescription");
    },
    prereqs: [],
    get auth() {
      return translate(libLabelsMessages, "codexAuth");
    },
    commands: {
      macos: "curl -fsSL https://chatgpt.com/codex/install.sh | sh",
      linux: "curl -fsSL https://chatgpt.com/codex/install.sh | sh",
      windows: "irm https://chatgpt.com/codex/install.ps1 | iex",
    },
    loginCommand: "codex login",
    docsUrl: "https://help.openai.com/en/articles/11096431",
  },
  opencode: {
    id: "opencode",
    name: "OpenCode",
    get description() {
      return translate(libLabelsMessages, "opencodeDescription");
    },
    prereqs: [],
    get auth() {
      return translate(libLabelsMessages, "opencodeAuth");
    },
    commands: {
      macos: "curl -fsSL https://opencode.ai/install | bash",
      linux: "curl -fsSL https://opencode.ai/install | bash",
      windows: OPENCODE_WINDOWS_INSTALL_COMMAND,
    },
    loginCommand: "opencode auth login",
    docsUrl: "https://opencode.ai/docs",
  },
};

/** Ordered list of CLIs for iteration. */
export const CLI_LIST: CliInstallInfo[] = [
  CLI_REGISTRY.claude,
  CLI_REGISTRY.codex,
  CLI_REGISTRY.opencode,
];
